import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * TravelMap
 * ----------------------------------------------------------------------
 * Compact Leaflet map that shows the client destination + one marker per
 * collaborator origin (currently the company origin), with polylines
 * connecting each origin to the client. Meant to live inside a modal or
 * side panel to visualize distances at a glance.
 *
 * Props
 *   clientCoords : [lng, lat] — the request's destination
 *   collabs      : [{id, name, avatar_url, color, origin_coords, travel_km, travel_minutes, is_available}]
 *   height       : css height for the map container (default 260px)
 */

// Fix default Leaflet icons which reference broken CDN paths in some bundlers.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Simple emoji-based divIcons so we don't need to ship custom PNGs
const clientIcon = L.divIcon({
  html: `<div style="background:#7c3aed;color:#fff;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(124,58,237,0.4);font-size:16px;border:2px solid #fff">📍</div>`,
  className: '',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const collabIcon = (letter, color = '#0f766e', greyed = false) => L.divIcon({
  html: `<div style="background:${greyed ? '#9ca3af' : color};color:#fff;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 5px rgba(0,0,0,0.15);font-weight:700;border:2px solid #fff;font-size:13px">${letter}</div>`,
  className: '',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

export function TravelMap({ clientCoords, collabs = [], height = 260 }) {
  // Convert [lng, lat] → [lat, lng] for Leaflet
  const toLatLng = (c) => (c && c.length >= 2 ? [c[1], c[0]] : null);

  const clientLatLng = toLatLng(clientCoords);

  const routes = useMemo(() => {
    return (collabs || [])
      .map((c, idx) => {
        const origin = toLatLng(c.origin_coords);
        if (!origin || !clientLatLng) return null;
        return {
          ...c,
          origin,
          letter: (c.name || '?').charAt(0).toUpperCase(),
          idx,
        };
      })
      .filter(Boolean);
  }, [collabs, clientLatLng]);

  // De-jitter origins that share the exact same coord so the markers don't
  // stack (typically all collabs share the company origin).
  const routesWithJitter = useMemo(() => {
    const groups = new Map();
    routes.forEach((r) => {
      const key = `${r.origin[0].toFixed(5)}_${r.origin[1].toFixed(5)}`;
      groups.set(key, (groups.get(key) || 0) + 1);
    });
    const seen = new Map();
    return routes.map((r) => {
      const key = `${r.origin[0].toFixed(5)}_${r.origin[1].toFixed(5)}`;
      const total = groups.get(key) || 1;
      if (total === 1) return r;
      const seenCount = seen.get(key) || 0;
      seen.set(key, seenCount + 1);
      // Small circular jitter (~150m radius depending on zoom)
      const angle = (seenCount / total) * 2 * Math.PI;
      const radius = 0.0015; // ~150m at mid-latitude
      return {
        ...r,
        origin: [r.origin[0] + radius * Math.cos(angle), r.origin[1] + radius * Math.sin(angle)],
      };
    });
  }, [routes]);

  const bounds = useMemo(() => {
    const pts = [];
    if (clientLatLng) pts.push(clientLatLng);
    routesWithJitter.forEach((r) => pts.push(r.origin));
    return pts.length ? pts : null;
  }, [clientLatLng, routesWithJitter]);

  if (!clientLatLng) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-4 text-xs text-gray-400 text-center" style={{ height }}>
        Aucune coordonnée pour afficher la carte.
      </div>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height }} data-testid="travel-map">
      <MapContainer
        style={{ width: '100%', height: '100%' }}
        bounds={bounds}
        boundsOptions={{ padding: [24, 24] }}
        scrollWheelZoom={false}
        attributionControl={false}
      >
        <TileLayer
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* Client destination */}
        <Marker position={clientLatLng} icon={clientIcon}>
          <Popup>
            <strong>Client</strong>
          </Popup>
        </Marker>
        {/* Collab origins + polylines */}
        {routesWithJitter.map((r) => (
          <React.Fragment key={r.id}>
            <Polyline
              positions={[r.origin, clientLatLng]}
              pathOptions={{
                color: r.is_available === false ? '#9ca3af' : (r.color || '#0f766e'),
                weight: 2.5,
                opacity: 0.7,
                dashArray: r.is_available === false ? '6 6' : undefined,
              }}
            />
            <Marker position={r.origin} icon={collabIcon(r.letter, r.color, r.is_available === false)}>
              <Popup>
                <div className="text-sm">
                  <strong>{r.name}</strong>
                  {r.travel_minutes != null && (
                    <div>🚗 {r.travel_minutes < 60 ? `${r.travel_minutes} min` : `${Math.floor(r.travel_minutes / 60)}h${String(r.travel_minutes % 60).padStart(2, '0')}`}{r.travel_km != null && ` · ${r.travel_km} km`}</div>
                  )}
                  {r.is_available === false && (<div className="text-red-600">Indisponible sur ce créneau</div>)}
                </div>
              </Popup>
            </Marker>
          </React.Fragment>
        ))}
      </MapContainer>
    </div>
  );
}

export default TravelMap;
